import audioUtil from './audio';
import { Track, TrackAudio, TrackText } from '../types';
import { EpubJsSpineItem } from '../types/epubjs';

const SMIL_FILE_FORMAT = /(.+)\/[^/]+$/;

type TextNode = {
  nodeName: 'text';
  src: string;
  srcFile: string;
  srcFragmentId: string;
};

type AudioNode = {
  nodeName: 'audio';
  src: string;
  clipBegin: string;
  clipEnd: string;
};

type ParNode = {
  nodeName: 'par';
  children: (TextNode | AudioNode)[];
};

type SmilNode = ParNode | TextNode | AudioNode;

function traverseChildNodes(element: Element, callbackOnParElement: (par: ParNode) => void): (TextNode | AudioNode)[] {
  const children: (TextNode | AudioNode)[] = [];
  (Array.from(element.childNodes) || [])
    .filter((childNode) => childNode.nodeType === Node.ELEMENT_NODE)
    .forEach((childNode) => {
      const item = createItemFromElement(childNode as Element, callbackOnParElement);
      if (item) {
        if (item.nodeName === 'par') {
          callbackOnParElement(item);
        } else {
          children.push(item);
        }
      }
    });

  return children;
}

function copyAttributes(item: Record<string, any>, element: Element, attributes: string[]) {
  attributes.forEach((attribute) => {
    const attributeValue = element.getAttribute(attribute);
    if (attributeValue) {
      item[attribute] = attributeValue;
    }
  });
  return item;
}

function createItemFromElement(element: Element, callbackOnParElement: (par: ParNode) => void): SmilNode | null {
  switch (element.nodeName.toLowerCase()) {
    case 'body':
    case 'seq':
    case 'par':
      return {
        nodeName: 'par',
        children: traverseChildNodes(element, callbackOnParElement),
      };
    case 'text': {
      const item: Partial<TextNode> = {
        nodeName: 'text',
      };
      copyAttributes(item, element, ['src']);
      if (item.src) {
        if (item.src.indexOf('#') !== -1) {
          [item.srcFile, item.srcFragmentId] = item.src.split('#');
        } else {
          [item.srcFile, item.srcFragmentId] = [item.src, item.src.split('.')[0]];
        }
      }
      return item as TextNode;
    }
    case 'audio': {
      const item: Partial<AudioNode> = {
        nodeName: 'audio',
      };
      copyAttributes(item, element, ['src', 'clipBegin', 'clipEnd']);
      return item as AudioNode;
    }
    default:
      return null;
  }
}

const audioNodeToTrackAudio = (
  node: AudioNode,
  smilPath: string,
  getBookResourceUrl: (path: string) => string,
): TrackAudio => {
  const additionalSmilPath = smilPath ? `${smilPath}/` : '';
  const filePath = `${additionalSmilPath}${node.src}`;
  const audioSrc = filePath.split('/');
  const fileName = encodeURIComponent(audioSrc.pop()!); // TODO move encodeUri into getBookResourceUrl?
  const encodedAudioSrc = audioSrc.concat(fileName).join('/');

  const audioUrl = getBookResourceUrl(encodedAudioSrc);
  const clipBegin = audioUtil.timeStrToNumber(node.clipBegin);
  const clipEnd = audioUtil.timeStrToNumber(node.clipEnd);

  return {
    ...node,
    url: audioUrl,
    clipBegin,
    clipEnd,
  };
};
const textNodeToTrackText = (node: TextNode, smilPath: string): TrackText => {
  const relativeSmilPath = node.src.replace(/^..\//g, '');
  const additionalSmilPath = smilPath && !relativeSmilPath.startsWith(smilPath) ? `${smilPath}/` : '';
  const src = `${additionalSmilPath}${relativeSmilPath}`;
  const srcFile = `${additionalSmilPath}${relativeSmilPath}`;
  return {
    ...node,
    src,
    srcFile,
  };
};

function getSmilFilePath(fullPath: string): string {
  return fullPath.match(SMIL_FILE_FORMAT) ? fullPath.match(SMIL_FILE_FORMAT)![1] : '';
}

export function getTracksFromSmilDocument(
  smilDocument: string,
  smilFilePath: string,
  textFilePath: string,
  getBookResourceUrl: (path: string) => string,
): Track[] {
  const smilElement = new DOMParser().parseFromString(smilDocument, 'text/xml').getElementsByTagName('smil')[0];
  if (!smilElement) {
    console.error(`The smil file ${smilFilePath} doesn't contain a <smil> tag, ignoring`);
    return [];
  }

  const tracks: Track[] = [];
  const smilPath = getSmilFilePath(smilFilePath);
  const textPath = getSmilFilePath(textFilePath);

  traverseChildNodes(smilElement, (parElement: ParNode) => {
    const textNode = parElement.children.find((child) => child.nodeName === 'text') as TextNode | undefined;
    const audioNodes = parElement.children.filter((child) => child.nodeName === 'audio') as AudioNode[];

    if (!textNode || audioNodes.length === 0) {
      return;
    }

    const text: TrackText = textNodeToTrackText(textNode, textPath);
    audioNodes.forEach((audioNode: AudioNode) => {
      const audio = audioNodeToTrackAudio(audioNode, smilPath, getBookResourceUrl);
      tracks.push({
        text,
        audio,
      });
    });
  });

  return tracks;
}

export function getSmilPathForItemByHref(href: string, spineItems: EpubJsSpineItem[]): string {
  /**
   * Added `spineItem.href !== href` check to verify if there is difference and if changes are needed.
   */
  const spineItem = spineItems.find((sItem) => sItem.href && sItem.href.includes(href) && sItem.href !== href);
  if (spineItem && spineItem.href && spineItem.href !== href) {
    return getSmilFilePath(spineItem.href);
  }
  return '';
}
